import profile from '../assets/author.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowRight, faAward, faCloud, faShieldHalved, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { faGithub, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

export default function Hero() {
  const metrics = [
    { label: "Experience", value: "14+ Years", detail: "DevOps & Cloud Platform Leadership", icon: faCloud },
    { label: "Credentials", value: "13+", detail: "GCP, Azure, Claude, Terraform, Wiz", icon: faAward },
    { label: "Multi-Cloud", value: "GCP · Azure · AWS", detail: "Production Multi-Region Systems", icon: faGlobe },
    { label: "Architecture", value: "Zero-Trust & SRE", detail: "Terraform · GitOps · K8s · Vault", icon: faShieldHalved }
  ];

  return (
    <section id="home" className="pt-12 pb-16 md:pt-20 md:pb-24 text-white overflow-hidden">
      {/* Hero Main Content */}
      <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-16">
        
        {/* Left Column: Intro Text & CTA */}
        <div className="flex-1 space-y-6">
          
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-teal-500/10 text-teal-300 border border-teal-500/30">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span>Staff Cloud Platform Engineer & Architect</span>
          </div>

          {/* Heading */}
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight break-words">
              Hi, I'm <span className="bg-gradient-to-r from-teal-400 via-cyan-300 to-emerald-400 bg-clip-text text-transparent">Syam R S</span>
            </h1>
            <h2 className="text-lg sm:text-2xl font-semibold text-gray-300 leading-snug">
              Staff Cloud Platform Engineer &middot; DevOps &middot; SRE &middot; Cloud Architect
            </h2>
          </div>

          {/* Narrative Bio */}
          <p className="text-sm sm:text-lg text-gray-300 leading-relaxed max-w-2xl font-normal">
            With over <strong className="text-white font-semibold">14 years</strong> in cloud architecture and platform engineering, I specialize in automating, hardening, and scaling resilient multi-cloud infrastructure across <strong className="text-teal-300 font-semibold">Google Cloud, Microsoft Azure, and AWS</strong>.
          </p>

          <p className="text-xs sm:text-base text-gray-400 leading-relaxed max-w-2xl font-light">
            I lead enterprise initiatives spanning Kubernetes platform engineering, Infrastructure as Code (Terraform &amp; Terragrunt), GitOps delivery (ArgoCD &amp; Atlantis), Zero-Trust secret management (HashiCorp Vault), and modern AI-Assisted engineering with <strong className="text-gray-200">Claude Code</strong>, <strong className="text-gray-200">Gemini</strong>, and <strong className="text-gray-200">GitHub Copilot</strong>.
          </p>

          {/* CTAs */}
          <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-3 pt-2">
            <a
              href="#projects"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-dark-900 font-bold px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all duration-200 transform hover:-translate-y-0.5 text-xs sm:text-sm"
            >
              <span>Featured Architecture</span>
              <FontAwesomeIcon icon={faArrowRight} className="text-xs sm:text-sm" />
            </a>

            <a
              href="#experience"
              className="inline-flex items-center justify-center gap-2 bg-dark-200 hover:bg-dark-100 text-white font-medium px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl border border-zinc-700/80 hover:border-zinc-500 transition-all duration-200 text-xs sm:text-sm"
            >
              <span>Work Experience</span>
            </a>

            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-1.5 text-gray-300 hover:text-teal-300 font-medium px-3 py-2 text-xs sm:text-sm transition-colors"
            >
              <span>Get In Touch &rarr;</span>
            </a>
          </div>

          {/* Social Links */}
          <div className="pt-2 flex items-center gap-4">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Connect:</span>
            <div className="flex items-center gap-3">
              <a
                href="https://github.com/syam-rs"
                rel="noreferrer"
                target="_blank"
                className="w-10 h-10 rounded-lg bg-dark-200/90 border border-zinc-700/70 hover:border-teal-400/80 flex items-center justify-center text-gray-300 hover:text-teal-400 hover:bg-dark-100 transition-all"
                title="GitHub"
              >
                <FontAwesomeIcon size="lg" icon={faGithub} />
              </a>

              <a
                href="https://www.linkedin.com/in/syam-r-s01/"
                rel="noreferrer"
                target="_blank"
                className="w-10 h-10 rounded-lg bg-dark-200/90 border border-zinc-700/70 hover:border-teal-400/80 flex items-center justify-center text-gray-300 hover:text-teal-400 hover:bg-dark-100 transition-all"
                title="LinkedIn"
              >
                <FontAwesomeIcon size="lg" icon={faLinkedinIn} />
              </a>

              <a
                href="mailto:syam.rs01@gmail.com"
                className="w-10 h-10 rounded-lg bg-dark-200/90 border border-zinc-700/70 hover:border-teal-400/80 flex items-center justify-center text-gray-300 hover:text-teal-400 hover:bg-dark-100 transition-all"
                title="Email Syam"
              >
                <FontAwesomeIcon size="lg" icon={faEnvelope} />
              </a>

              <a
                href="https://syam.one"
                rel="noreferrer"
                target="_blank"
                className="w-10 h-10 rounded-lg bg-dark-200/90 border border-zinc-700/70 hover:border-teal-400/80 flex items-center justify-center text-gray-300 hover:text-teal-400 hover:bg-dark-100 transition-all"
                title="Personal Domain"
              >
                <FontAwesomeIcon size="lg" icon={faGlobe} />
              </a>
            </div>
          </div>

        </div>

        {/* Right Column: Profile Image with Glow Backdrop */}
        <div className="relative flex justify-center items-center">
          {/* Subtle Ambient Glow */}
          <div className="absolute -inset-2 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 rounded-3xl blur-2xl opacity-70"></div>
          
          <div className="relative p-2 bg-dark-200/80 border border-zinc-700/80 rounded-3xl backdrop-blur-sm shadow-2xl">
            <img
              src={profile}
              alt="Syam R S - Staff Cloud Platform Engineer"
              className="w-64 h-64 sm:w-80 sm:h-80 object-cover rounded-2xl border border-zinc-800"
            />
            {/* Overlay pill badge */}
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-dark-300/95 border border-teal-500/40 backdrop-blur-md px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2 whitespace-nowrap">
              <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              <span className="text-xs font-semibold text-white tracking-wide">14+ Years Cloud &amp; DevOps</span>
            </div>
          </div>
        </div>

      </div>

      {/* Metrics Banner */}
      <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((item, idx) => (
          <div
            key={idx}
            className="p-5 rounded-2xl bg-dark-200/60 border border-zinc-800/80 hover:border-teal-500/40 transition-all duration-300 hover:-translate-y-1 group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400 group-hover:bg-teal-500/20 transition-colors">
                <FontAwesomeIcon icon={item.icon} className="text-sm" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{item.label}</span>
            </div>
            <div className="text-2xl font-bold text-white tracking-tight">{item.value}</div>
            <div className="text-xs text-gray-400 mt-1">{item.detail}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
